import * as React from 'react';
import bindAllOfThis from 'src/utils/BindThisHelper';
import { Row, Col } from "antd";
import DialogBox from "src/components/mission/DialogBox";
import { EnhancedProps } from "src/pages/student/models/props";
import ActivityApiController from "src/api/ActivityApiController";
import StudentActivityDTO from "src/models/activity/StudentActivityDTO";
import ActivityIntro from "src/components/student/ActivityIntro";
import ActivitySummary from "src/components/student/ActivitySummary";
import ActivityConclusion from 'src/components/student/ActivityConclusion';
import StudentActivityUserQuestionResponseDTO from "src/models/activity/StudentActivityUserQuestionResponseDTO";
import ActivityQuestion from "src/components/student/ActivityQuestion";
import StudentActivitySummaryDTO from "src/models/activity/StudentActivitySummaryDTO";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";

interface ActivityWrapper<T> {
    activityId: number;
    data: T;
}

interface StudentActivityProps extends EnhancedProps, LocalizeContextProps {
}

interface StudentActivityState {
    activityCounter: number;
    activityStep: number;
    activities: StudentActivityDTO[];

    studentResponses: ActivityWrapper<StudentActivityUserQuestionResponseDTO[]>[];
    studentSummary: StudentActivitySummaryDTO;
    isSubmitting: boolean;
    /** Indicates that there is a load operation happening */
    isLoading: boolean;
}

class StudentActivity extends React.Component<StudentActivityProps, StudentActivityState> {
    constructor(props: StudentActivityProps) {
        super(props);
        bindAllOfThis(this, StudentActivity.prototype);

        this.state = {
            activityCounter: 0,
            activityStep: -1,
            activities: [],
            studentResponses: [],
            studentSummary: null,
            isSubmitting: false,
            isLoading: false,
        };
    }

    componentDidMount(): void {
        this.deriveStateFromProps(this.props);
    }

    componentWillReceiveProps(nextProps: StudentActivityProps) {
        if (this.props.activeLanguage !== nextProps.activeLanguage ||
            this.props.User.gradeId !== nextProps.User.gradeId ||
            this.props.Mission.current.planetId !== nextProps.Mission.current.planetId) {
            // Update the translation array
            this.deriveStateFromProps(nextProps);
        }
    }

    deriveStateFromProps(newProps: StudentActivityProps) {
        ActivityApiController.getStudentActivities({ gradeId: newProps.currentGrade.id, planetCode: newProps.currentPlanet.name }).then(result => {
            if (result == null || result.data.length < 1) {
                this.props.onNext();
                return;
            }
            this.setState({
                activities: result.data
            });

            this.pickNextDisplay();

            result.data.forEach(activity => {
                ActivityApiController.getStudentActivityResponses(activity.id).then(studentResponse => {
                    // We can't be 100% sure what order they will return, so we need to use the fancy setState() method
                    this.setState((newState) => {
                        let newResponse = { activityId: activity.id, data: studentResponse.data.items };
                        return { studentResponses: [...newState.studentResponses, newResponse] };
                    });
                });
            });
        });
    }

    hasNextButton(): boolean {
        return this.state.activityStep !== 1;
    }

    hasBackButton(): boolean {
        return this.state.activityStep < 1;
    }

    pickNextDisplay() {
        let step = this.state.activityStep;
        let counter = this.state.activityCounter;

        do {
            step++;
            if (step > 3) {
                // Overflow! inc counter
                counter++;
                step = 0;
            }
            if (counter > this.state.activities.length) {
                // Finished!
                return this.props.onNext();
            }
        } while (!this.isValidActivityStep(step, counter));

        this.setState({
            activityStep: step,
            activityCounter: counter,
        });
    }

    pickPreviousDisplay() {
        let step = this.state.activityStep;
        let counter = this.state.activityCounter;

        do {
            step--;
            if (step < 0) {
                // Underflow! dec counter
                counter--;
                step = 3;
            }
            if (counter < 0) {
                // Goto Previous page
                return this.props.onBack();
            }
        } while (!this.isValidActivityStep(step, counter));

        this.setState({
            activityStep: step,
            activityCounter: counter,
        });
    }

    isValidActivityStep(step: number, counter: number) {
        // Check intro, summary and conclusion
        if (this.state.activities == null || this.state.activities.length < 1) {
            return false;
        }
        let activity = this.state.activities[counter];
        if (activity == null) {
            return false;
        }
        switch (step) {
            case 0: // Intro
                return activity.introduction != null && activity.introduction.text != null && activity.introduction.text.trim().length > 0;
            case 1: // Questions
                return activity.questions != null && activity.questions.length > 0;
            case 2: // Summary
                return this.state.studentSummary != null && this.state.studentSummary.text != null && this.state.studentSummary.text.trim().length > 0;
            case 3: // Conclusion
                return activity.conclusion != null && activity.conclusion.text != null && activity.conclusion.text.trim().length > 0;
            default:
                break;
        }
    }

    handleQuestionSubmit(studentResponses: StudentActivityUserQuestionResponseDTO[]) {
        let currentActivity = this.state.activities[this.state.activityCounter];
        this.setState({ isSubmitting: true });
        ActivityApiController.submitStudentActivity({ activityId: currentActivity.id, responses: studentResponses }).then(() => {
            this.setState({
                isSubmitting: false,
                isLoading: true,
            });

            // Get summary for this student/activity
            ActivityApiController.getStudentActivitySummary(currentActivity.id).then(result => {
                // Data comes back as null when there is no summary. This is OK as it will reset the studentSummary to null
                this.setState({
                    studentSummary: result.data,
                    isLoading: false,
                }, () => this.pickNextDisplay());
            });
        });
    }

    renderDialog() {
        if (this.state.activities == null || this.state.activities.length < 1) {
            return null;
        }

        let currentActivity = this.state.activities[this.state.activityCounter];
        let studentResponses = this.state.studentResponses.find(x => x.activityId === currentActivity.id);
        if (currentActivity == null || studentResponses == null) {
            return null;
        }

        let props = {
            activity: currentActivity,
            currentGame: this.props.currentGame,
            onNext: this.pickNextDisplay,
            onBack: this.pickPreviousDisplay,
        };

        return <DialogBox
            currentGrade={this.props.currentGrade}
            currentPlanet={this.props.currentPlanet}
            buttonNextType={this.hasNextButton() ? "arrow" : "none"}
            buttonBackType={this.hasBackButton() ? "arrow" : "none"}
            onNext={this.pickNextDisplay}
            onBack={this.pickPreviousDisplay}>
            <div>
                {/* {this.renderActivityStepSwitch(this.state.activityStep, props, studentResponses.data)} */}
                {this.state.activityStep === 0 ? <ActivityIntro {...props} /> : null}
                {this.state.activityStep === 1 ? <ActivityQuestion {...props} onFinish={this.handleQuestionSubmit} studentResponses={studentResponses.data} isSubmitting={this.state.isSubmitting} /> : null}
                {this.state.activityStep === 2 ? <ActivitySummary {...props} summary={this.state.studentSummary} /> : null}
                {this.state.activityStep === 3 ? <ActivityConclusion {...props} /> : null}
            </div>
        </DialogBox>;
    }

    renderActivityStepSwitch(activityStep: number, props: any, studentResponsesdata: any) {
        switch (activityStep) {
            case 0:
                if (this.isValidActivityStep(0, 0)) {
                    return <ActivityIntro {...props} />;
                } else {
                    return <ActivityQuestion {...props} onFinish={this.handleQuestionSubmit} studentResponses={studentResponsesdata} isSubmitting={this.state.isSubmitting} />;
                }
            case 1:
                return <ActivityQuestion {...props} onFinish={this.handleQuestionSubmit} studentResponses={studentResponsesdata} isSubmitting={this.state.isSubmitting} />;
            case 2:
                return <ActivitySummary {...props} summary={this.state.studentSummary} />;
            case 3:
                return <ActivityConclusion {...props} />;
            default:
                return null;
        }
    }

    render() {
        let planetCode: string = (this.props.currentPlanet || { name: "" }).name.toLowerCase() as string;

        this.props.onChangeAudio("");

        return <div>
            <Row>
                <Col className="dialog-content" xs={{ span: 22, push: 1 }} md={{ span: 16 }} lg={16} xl={14}>
                    <Row>
                        {this.renderDialog()}
                    </Row>
                </Col>
            </Row>
            <div className={`star-pilot star-pilot-${planetCode}`} />
            <div className={`star-pod star-pod-landed star-pod-${planetCode}-landed`} />
            <div className={`planet-terrain planet-terrain-${planetCode}`} />
        </div>;
    }
}

export default withLocalize(StudentActivity);



// WEBPACK FOOTER //
// ./src/pages/student/scenes/StudentActivity.tsx